// External dependencies
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

// Internal dependencies
import AdminLayout from '@/modules/admin/client/components/AdminLayout.component';
import CmsAdminNav from '@/modules/cms/client/components/CmsAdminNav.component';
import CmsEditor from '@/modules/cms/client/components/CmsEditor.component';
import {
  createPage,
  createPost,
  getBlogCategories,
  getPage,
  getRevisions,
  restoreRevision,
  updatePage,
  uploadImage,
} from '@/modules/cms/client/api/admin-cms.api';
import { isBlogCategoriesEnabled } from '@/modules/cms/client/utils/feature-flags';
import { getLocales } from '@/modules/core/client/utils/locales';

const EMPTY_PAGE = {
  title: '',
  slug: '',
  locale: 'en',
  body: '',
  excerpt: '',
  status: 'draft',
  publishAt: '',
  metaTitle: '',
  metaDescription: '',
  tags: [],
  coverImage: '',
  // Category id, or '' for none. Posts only.
  category: '',
};

/**
 * What differs between editing a page and editing a post.
 */
const KIND_CONFIG = {
  page: {
    singular: 'page',
    adminBase: '/admin/cms/pages',
    listPath: '/admin/cms',
    publicBase: '/pages',
    backLabel: 'Back to pages',
    create: createPage,
    publishAtLabel: 'Publish at',
    publishAtHelp: 'Optional. A published page stays hidden until this moment.',
  },
  post: {
    singular: 'post',
    adminBase: '/admin/cms/posts',
    listPath: '/admin/cms/posts',
    publicBase: '/blog',
    backLabel: 'Back to posts',
    create: createPost,
    publishAtLabel: 'Post date',
    publishAtHelp:
      'Shown on the post and used to order the blog. Left blank, set to the moment the post is first published. A future date schedules the post.',
  },
};

/**
 * Convert an ISO date into the value a `datetime-local` input expects.
 *
 * @param {String} value - ISO date string
 * @returns {String}
 */
function toLocalInputValue(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return '';
  }

  // `toISOString` is UTC; shift by the offset so the input shows local time.
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

/**
 * Shape a loaded or saved document for the form state.
 *
 * @param {Object} data - Page document from the API
 * @returns {Object}
 */
function toFormState(data) {
  return {
    ...EMPTY_PAGE,
    ...data,
    publishAt: toLocalInputValue(data.publishAt),
    tags: Array.isArray(data.tags) ? data.tags : [],
    coverImage: data.coverImage || '',
    // The admin list populates `category`; the by-id read returns the raw id.
    category: data.category ? String(data.category._id || data.category) : '',
  };
}

/**
 * Split a comma-separated tag string into a clean list.
 *
 * @param {String} text - Raw input value
 * @returns {Array<String>}
 */
function parseTags(text) {
  return text
    .split(',')
    .map(tag => tag.trim())
    .filter(Boolean);
}

/**
 * Create or edit a CMS page or blog post.
 *
 * `pageId` is absent when creating. `kind` picks the labels, URLs and the
 * post-only fields; when editing, the loaded document's own kind wins, so a
 * post opened through the page URL is still edited as a post.
 */
export default function AdminCmsPageEdit({ pageId, kind: kindProp }) {
  const isEditing = Boolean(pageId);

  const [page, setPage] = useState(EMPTY_PAGE);
  const [tagsText, setTagsText] = useState('');
  const [revisions, setRevisions] = useState([]);
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [categories, setCategories] = useState([]);
  // The rich text toolbar cannot reach every allowed tag (tables, figures),
  // so editors need a way to the raw markup. Both modes write to `body`.
  const [isSourceMode, setIsSourceMode] = useState(false);

  const kind = (isEditing && page.kind) || kindProp || 'page';
  const config = KIND_CONFIG[kind] || KIND_CONFIG.page;
  const isPost = kind === 'post';

  const locales = getLocales();

  // Posts pick from the managed category list. All languages are loaded so
  // the select can show (and keep) a category from another language; the
  // post's own language is listed first.
  useEffect(() => {
    if (!isPost || !isBlogCategoriesEnabled()) {
      return;
    }
    getBlogCategories()
      .then(setCategories)
      // The picker is supplementary; a failure must not blank the form.
      .catch(() => setCategories([]));
  }, [isPost]);

  const categoryOptions = [...categories].sort((a, b) => {
    const aOwn = a.locale === page.locale ? 0 : 1;
    const bOwn = b.locale === page.locale ? 0 : 1;
    return aOwn - bOwn || a.order - b.order || a.name.localeCompare(b.name);
  });

  const loadRevisions = id => {
    getRevisions(id)
      .then(setRevisions)
      // Revisions are supplementary; a failure here should not blank the form.
      .catch(() => setRevisions([]));
  };

  const applyLoaded = data => {
    const state = toFormState(data);
    setPage(state);
    setTagsText(state.tags.join(', '));
  };

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    setIsLoading(true);

    getPage(pageId)
      .then(data => {
        applyLoaded(data);
        loadRevisions(pageId);
      })
      .catch(() => setError(`Failed to load ${config.singular}.`))
      .finally(() => setIsLoading(false));
  }, [pageId]);

  const setField = (name, value) => {
    setPage(previous => ({ ...previous, [name]: value }));
  };

  const handleCoverUpload = async event => {
    const [file] = event.target.files || [];
    // Allow picking the same file again after a failure.
    event.target.value = '';

    if (!file) {
      return;
    }

    setIsUploadingCover(true);
    setError(null);

    try {
      const { url } = await uploadImage(file);
      setField('coverImage', url);
    } catch (err) {
      const message =
        err.response && err.response.data && err.response.data.message;
      setError(message || 'Failed to upload cover image.');
    } finally {
      setIsUploadingCover(false);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    const payload = {
      title: page.title,
      slug: page.slug,
      locale: page.locale,
      body: page.body,
      excerpt: page.excerpt,
      status: page.status,
      metaTitle: page.metaTitle,
      metaDescription: page.metaDescription,
      // An empty input clears the schedule; the server reads `null` as such.
      publishAt: page.publishAt ? new Date(page.publishAt).toISOString() : null,
    };

    if (isPost) {
      payload.tags = parseTags(tagsText);
      payload.coverImage = page.coverImage.trim();
      // An empty selection clears the category; the server reads `null`.
      payload.category = page.category || null;
    }

    try {
      if (isEditing) {
        const saved = await updatePage(pageId, payload);
        applyLoaded(saved);
        loadRevisions(pageId);
        setSuccess(`${isPost ? 'Post' : 'Page'} saved.`);
      } else {
        const created = await config.create(payload);
        setSuccess(`${isPost ? 'Post' : 'Page'} created.`);
        // Move to the edit URL so a refresh does not create a second one.
        window.location.href = `${config.adminBase}/${created._id}`;
      }
    } catch (err) {
      const message =
        err.response && err.response.data && err.response.data.message;
      setError(message || `Failed to save ${config.singular}.`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestore = async revision => {
    const confirmed = window.confirm(
      `Restore the version saved on ${new Date(
        revision.createdAt,
      ).toLocaleString()}? The current version is kept as a new revision.`,
    );

    if (!confirmed) {
      return;
    }

    try {
      const restored = await restoreRevision(pageId, revision._id);
      applyLoaded(restored);
      loadRevisions(pageId);
      setSuccess('Version restored.');
    } catch (err) {
      setError('Failed to restore this version.');
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="container">
          <p>Loading {config.singular}…</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <CmsAdminNav />
      <div className="container">
        <div className="row" style={{ marginBottom: '1rem' }}>
          <div className="col-sm-9">
            <h2>
              {isEditing ? 'Edit' : 'New'} {config.singular}
            </h2>
            {isEditing && page.slug && (
              <p>
                Public URL:{' '}
                <code>
                  {config.publicBase}/{page.slug}
                </code>
              </p>
            )}
          </div>
          <div className="col-sm-3" style={{ marginTop: '20px' }}>
            <a href={config.listPath} className="btn btn-default btn-block">
              {config.backLabel}
            </a>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-sm-6">
              <div className="form-group">
                <label htmlFor="admin-cms-title">Title</label>
                <input
                  id="admin-cms-title"
                  type="text"
                  className="form-control"
                  value={page.title}
                  onChange={event => setField('title', event.target.value)}
                  required
                />
              </div>
            </div>
            <div className="col-sm-6">
              <div className="form-group">
                <label htmlFor="admin-cms-slug">Slug</label>
                <input
                  id="admin-cms-slug"
                  type="text"
                  className="form-control"
                  value={page.slug}
                  onChange={event => setField('slug', event.target.value)}
                  placeholder="Left blank, derived from the title"
                />
                <span className="help-block">
                  Lowercase, dashes only. Changing this keeps the old URL
                  working.
                </span>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-4">
              <div className="form-group">
                <label htmlFor="admin-cms-locale">Language</label>
                <select
                  id="admin-cms-locale"
                  className="form-control"
                  value={page.locale}
                  onChange={event => setField('locale', event.target.value)}
                >
                  {locales.map(locale => (
                    <option key={locale.code} value={locale.code}>
                      {locale.label} ({locale.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-sm-4">
              <div className="form-group">
                <label htmlFor="admin-cms-status">Status</label>
                <select
                  id="admin-cms-status"
                  className="form-control"
                  value={page.status}
                  onChange={event => setField('status', event.target.value)}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>
            <div className="col-sm-4">
              <div className="form-group">
                <label htmlFor="admin-cms-publish-at">
                  {config.publishAtLabel}
                </label>
                <input
                  id="admin-cms-publish-at"
                  type="datetime-local"
                  className="form-control"
                  value={page.publishAt}
                  onChange={event => setField('publishAt', event.target.value)}
                />
                <span className="help-block">{config.publishAtHelp}</span>
              </div>
            </div>
          </div>

          {isPost && isBlogCategoriesEnabled() && (
            <div className="row">
              <div className="col-sm-6">
                <div className="form-group">
                  <label htmlFor="admin-cms-category">Category</label>
                  <select
                    id="admin-cms-category"
                    className="form-control"
                    value={page.category}
                    onChange={event => setField('category', event.target.value)}
                  >
                    <option value="">— None —</option>
                    {categoryOptions.map(item => (
                      <option key={item._id} value={item._id}>
                        {item.name}
                        {item.locale !== page.locale ? ` (${item.locale})` : ''}
                        {item.enabled === false ? ' (hidden)' : ''}
                      </option>
                    ))}
                  </select>
                  <span className="help-block">
                    One per post. Readers browse by category from the blog
                    sidebar.{' '}
                    <a href="/admin/cms/blog-categories">Manage categories</a>
                  </span>
                </div>
              </div>
            </div>
          )}

          {isPost && (
            <div className="row">
              <div className="col-sm-6">
                <div className="form-group">
                  <label htmlFor="admin-cms-tags">Tags</label>
                  <input
                    id="admin-cms-tags"
                    type="text"
                    className="form-control"
                    value={tagsText}
                    onChange={event => setTagsText(event.target.value)}
                    placeholder="travel, hosting, ireland"
                  />
                  <span className="help-block">
                    Comma separated, up to 10. Readers can filter the blog by
                    tag.
                  </span>
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <label htmlFor="admin-cms-cover-image">Cover image</label>
                  <div className="input-group">
                    <input
                      id="admin-cms-cover-image"
                      type="text"
                      className="form-control"
                      value={page.coverImage}
                      onChange={event =>
                        setField('coverImage', event.target.value)
                      }
                      placeholder="Upload a file, or paste an https:// URL"
                    />
                    <span className="input-group-btn">
                      <label
                        className={
                          isUploadingCover
                            ? 'btn btn-default disabled'
                            : 'btn btn-default'
                        }
                        htmlFor="admin-cms-cover-upload"
                        style={{ marginBottom: 0 }}
                      >
                        {isUploadingCover ? 'Uploading…' : 'Upload'}
                      </label>
                      <input
                        id="admin-cms-cover-upload"
                        type="file"
                        accept="image/png,image/jpeg,image/gif"
                        className="sr-only"
                        disabled={isUploadingCover}
                        onChange={handleCoverUpload}
                      />
                    </span>
                  </div>
                  <span className="help-block">
                    Optional. Shown above the post, in the blog list and in link
                    previews. Uploads are resized to at most 1600px wide.
                  </span>
                  {page.coverImage &&
                    /^(https?:\/\/|\/(?!\/))/i.test(page.coverImage) && (
                      <img
                        src={page.coverImage}
                        alt=""
                        className="img-responsive img-thumbnail"
                        style={{ maxHeight: '120px' }}
                      />
                    )}
                </div>
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="admin-cms-body">Body</label>
            <button
              type="button"
              className="btn btn-link btn-xs pull-right"
              onClick={() => setIsSourceMode(previous => !previous)}
            >
              {isSourceMode ? 'Rich text' : 'Edit HTML source'}
            </button>
            {isSourceMode ? (
              <textarea
                id="admin-cms-body"
                className="form-control"
                rows="20"
                value={page.body}
                onChange={event => setField('body', event.target.value)}
                style={{ fontFamily: 'monospace' }}
              />
            ) : (
              <div className="panel panel-default">
                <div className="panel-body">
                  <CmsEditor
                    id="admin-cms-body"
                    value={page.body}
                    placeholder={`Write the ${config.singular} content`}
                    onChange={value => setField('body', value)}
                  />
                </div>
              </div>
            )}
            <span className="help-block">
              Markup is sanitized on save. Headings, lists, links, images and
              tables are kept; scripts, iframes and inline styles are removed.
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="admin-cms-excerpt">Excerpt</label>
            <textarea
              id="admin-cms-excerpt"
              className="form-control"
              rows="2"
              value={page.excerpt}
              onChange={event => setField('excerpt', event.target.value)}
              placeholder="Left blank, derived from the body"
            />
            {isPost && (
              <span className="help-block">Shown in the blog list.</span>
            )}
          </div>

          <fieldset>
            <legend className="h4">Search engines</legend>
            <div className="row">
              <div className="col-sm-6">
                <div className="form-group">
                  <label htmlFor="admin-cms-meta-title">Meta title</label>
                  <input
                    id="admin-cms-meta-title"
                    type="text"
                    className="form-control"
                    value={page.metaTitle}
                    onChange={event =>
                      setField('metaTitle', event.target.value)
                    }
                    placeholder="Left blank, falls back to the title"
                  />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <label htmlFor="admin-cms-meta-description">
                    Meta description
                  </label>
                  <input
                    id="admin-cms-meta-description"
                    type="text"
                    className="form-control"
                    value={page.metaDescription}
                    onChange={event =>
                      setField('metaDescription', event.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          </fieldset>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSaving || !page.title}
          >
            {isSaving ? 'Saving…' : `Save ${config.singular}`}
          </button>
        </form>

        {isEditing && (
          <div style={{ marginTop: '2rem' }}>
            <h3>Version history</h3>
            <p className="help-block">
              The most recent 30 versions are kept. Restoring is itself undoable
              — the current version is saved first.
            </p>
            {revisions.length ? (
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Saved</th>
                    <th>Title at the time</th>
                    <th>By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {revisions.map(revision => (
                    <tr key={revision._id}>
                      <td>{new Date(revision.createdAt).toLocaleString()}</td>
                      <td>{revision.title}</td>
                      <td>
                        {(revision.revisedBy &&
                          (revision.revisedBy.displayName ||
                            revision.revisedBy.username)) ||
                          '—'}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-default btn-xs"
                          onClick={() => handleRestore(revision)}
                        >
                          Restore
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No earlier versions yet.</p>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

AdminCmsPageEdit.propTypes = {
  pageId: PropTypes.string,
  kind: PropTypes.oneOf(['page', 'post']),
};

AdminCmsPageEdit.defaultProps = {
  kind: 'page',
};
